import { useRouterState } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { useCallback, useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { HelpFeedbackDrawer } from "@/components/HelpFeedbackDrawer";
import { HelpFeedbackProvider } from "@/components/help-feedback-context";
import { Sidebar } from "@/components/Sidebar";
import { useCurrentUser } from "../auth/useCurrentUser";

export function Layout({ children }: { children: React.ReactNode }) {
  const { data, isAuthenticated, signoutRedirect } = useCurrentUser();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Declared before the early returns so the hook order is stable
  // (react-hooks/rules-of-hooks). Passed to HelpFeedbackProvider so routed
  // pages (e.g. /settings) can open the drawer.
  const openFeedback = useCallback(() => setFeedbackOpen(true), []);

  // Public auth pages (login / forgot-password / set-password / request
  // access flow / OIDC callback) render edge-to-edge with no app
  // chrome — they own their own visual frame per Epic 1's spec.
  // Unauthenticated routes use a path check rather than reading auth
  // state because of a brief flicker between mount and the /auth/me
  // query resolving.
  //
  // CHP-1021: read the path from the router's reactive location, NOT
  // `window.location.pathname`. Layout only re-subscribes to useCurrentUser, so
  // a one-off window read latches a stale path across the post-login redirect
  // cascade (/auth/callback → /dashboard → ProtectedRoute bounce →
  // /onboarding/identity), leaving the app chrome rendered behind the identity
  // step (the CHP-884 chromeless branch below then misses it). useRouterState
  // recomputes on every navigation, so the onboarding guard fires on landing.
  const path = useRouterState({ select: (s) => s.location.pathname });
  const chromeless =
    !isAuthenticated &&
    (path === "/login" ||
      path === "/forgot-password" ||
      path === "/set-password" ||
      path.startsWith("/request-access") ||
      path === "/auth/callback");
  // CHP-884: the post-invite onboarding wizard is AUTHENTICATED but still
  // chromeless — it owns its own full-screen frame (OnboardingShell). Without
  // this branch the app Sidebar + header render behind the wizard, so the user
  // can see (and the mobile drawer can reach) the full nav before they have
  // completed onboarding. Path-based so it applies on the first paint, before
  // /auth/me resolves — the wizard routes are guarded by ProtectedRoute anyway.
  const onboarding = path.startsWith("/onboarding");
  if (chromeless || onboarding) {
    return <>{children}</>;
  }

  if (!isAuthenticated || !data) {
    // Authenticated chrome requires a resolved user; while /auth/me is in
    // flight, render children edge-to-edge rather than flashing an empty
    // shell with placeholder sidebar.
    return <>{children}</>;
  }

  const handleSignOut = () => void signoutRedirect();

  return (
    <HelpFeedbackProvider openFeedback={openFeedback}>
      <div className="grid min-h-screen text-foreground lg:grid-cols-[240px_1fr]">
        <aside className="sticky top-0 hidden h-screen lg:block">
          <Sidebar data={data} onSignOut={handleSignOut} onOpenFeedback={openFeedback} />
        </aside>

        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="left" className="w-[240px] p-0">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">
              Sections and links for the application.
            </SheetDescription>
            <Sidebar
              data={data}
              onSignOut={handleSignOut}
              onNavigate={() => setDrawerOpen(false)}
              onOpenFeedback={openFeedback}
            />
          </SheetContent>
        </Sheet>

        <HelpFeedbackDrawer open={feedbackOpen} onOpenChange={setFeedbackOpen} />

        <div className="flex min-w-0 flex-col">
          <header className="sticky top-0 z-30 flex h-12 items-center justify-between gap-3 border-b border-border bg-background/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label="Open navigation"
              onClick={() => setDrawerOpen(true)}
            >
              <Menu className="h-5 w-5" aria-hidden />
            </Button>
            <span className="ml-auto flex min-w-0 items-center gap-2 rounded-full border border-border bg-card py-1 pl-1.5 pr-3 shadow-sm">
              {/* CHP-815: the header pill shows the uploaded avatar too, falling
                  back to the email initial — so a photo set in Settings updates
                  every current-user icon, not just the sidebar chip. */}
              <Avatar aria-hidden className="h-5 w-5 shrink-0">
                {data.avatar_url ? <AvatarImage src={data.avatar_url} alt="" /> : null}
                <AvatarFallback className="bg-primary text-[9px] font-semibold text-primary-foreground">
                  {data.email?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="truncate text-xs text-muted-foreground">{data.email}</span>
            </span>
          </header>
          <main className="min-w-0 flex-1">{children}</main>
        </div>
      </div>
    </HelpFeedbackProvider>
  );
}
